import React, { type ReactNode, useCallback, useEffect, useState } from 'react';

import { ErrorBoundary } from 'react-error-boundary';
import { loadEntryPoint } from 'react-relay';

import { cssMap } from '@atlaskit/css';
import type { Placement } from '@atlaskit/popper/main';
import { Popper as ReactPopper } from '@atlaskit/popper/main';
import Portal from '@atlaskit/portal/portal';
import { Box } from '@atlaskit/primitives/compiled';
import { layers } from '@atlaskit/theme/constants';
import { InternalEntryPointContainer } from '@atlassian/internal-entry-point-container';
import {
	AtlassianRelayEnvironmentProvider,
	useRelayEnvironmentProvider,
} from '@atlassian/relay-environment-provider';

import {
	entryPoint,
	type UserProfileCardRelayTriggerParams,
} from '../network/userProfileCard.entrypoint';
import type {
	CustomerCardDataLoader,
	PersonProfileData,
	UserProfileCardAnalyticsAttributes,
} from '../types';
import { getPageTime } from '../utils/performance';

import { CardError } from './components/CardError';
import { CardLoading } from './components/CardLoading';
import type { UserProfileCardRelayAgentProps } from './UserProfileCardRelay';
import {
	UserProfileCardWithTrigger,
	type UserProfileCardWithTriggerProps,
} from './UserProfileCardWithTrigger';

const ENTRY_POINT_ID = 'user-profile-card-for-mention';

const mentionPopupStyles = cssMap({
	// Once the mention is scrolled out of its scroll container, popper's `hide` modifier flags
	// the reference as hidden. Hide the card too so it disappears
	hidden: {
		visibility: 'hidden',
		pointerEvents: 'none',
	},
});

type EntryPointReference = ReturnType<typeof loadEntryPoint<typeof entryPoint>>;

type PopperContentProps = {
	entryPointReference: EntryPointReference;
	entryPointErrorFallback: (props: { error: Error; onClose: () => void }) => React.JSX.Element;
	cloudId: string;
	testId?: string;
	agent?: UserProfileCardRelayAgentProps;
	customerCardDataLoader?: CustomerCardDataLoader;
	isReduced?: boolean;
};

function PopperContent({
	entryPointReference,
	entryPointErrorFallback,
	cloudId,
	testId,
	agent,
	customerCardDataLoader,
	isReduced,
}: PopperContentProps): React.JSX.Element {
	return (
		<InternalEntryPointContainer
			id={ENTRY_POINT_ID}
			entryPointReference={entryPointReference}
			fallback={<CardLoading isReduced={isReduced} />}
			errorFallback={entryPointErrorFallback}
			runtimeProps={{ cloudId, testId, agent, customerCardDataLoader, isReduced }}
		/>
	);
}

/**
 * Props common to both render paths.
 */
type UserProfileCardForMentionCommonProps = UserProfileCardRelayTriggerParams & {
	/** The `@user` mention to wrap (renderer) or render alongside the card (editor). */
	children: ReactNode;
	/** Where to position the card relative to the mention. Defaults to `'bottom-start'`. */
	placement?: Placement;
	/** Test ID prefix for unit / VR tests. */
	testId?: string;
	/** Agent-card config, forwarded to `AgentCard` when the subject is a Rovo agent. Defaults to `{}`; pass custom actions (chat, delete, etc.) to override. */
	agent?: UserProfileCardRelayAgentProps;
	/**
	 * Renders a reduced, non-interactive card with the header + manager section only
	 */
	isReduced?: boolean;
	/**
	 * Consumer-supplied analytics attributes merged into every analytics
	 * event fired by the card via the card's shared analytics context.
	 */
	analyticsAttributes?: UserProfileCardAnalyticsAttributes;
	/** Known identity to prefill the card with (renderer path only), for a concrete accessible name. */
	prefilledProfileData?: PersonProfileData;
	/**
	 * @private
	 * @deprecated Temporary Jira portal-only email bridge while AGG omits site-scoped `CustomerUser.email`.
	 */
	customerCardDataLoader?: CustomerCardDataLoader;
	/** Called after the renderer profile-card popup opens. */
	onOpen?: (method: 'click' | 'hover') => void;
};

/**
 * Renderer (read-only) path: no `referenceElement`. The card opens via the
 * standard click / hover trigger on `children`, so `trigger` is allowed.
 */
type UserProfileCardForMentionRendererProps = UserProfileCardForMentionCommonProps & {
	referenceElement?: undefined;
	/**
	 * How the card opens. Defaults to `'click'`. Pass `['click', 'hover']` to
	 * also open on hover. Only valid in the renderer path - in the editor path
	 * the editor controls open / close by mounting / unmounting this
	 * component, so this prop is rejected at compile time.
	 */
	trigger?: UserProfileCardWithTriggerProps['trigger'];
};

/**
 * Editor / nodeview path: `referenceElement` is provided. The card stays open
 * while mounted, anchored to that element, so a `trigger` value would be
 * silently ignored - we forbid it at the type level to surface that mistake at
 * the call site.
 */
type UserProfileCardForMentionEditorProps = UserProfileCardForMentionCommonProps & {
	referenceElement: HTMLElement;
	trigger?: never;
};

export type UserProfileCardForMentionProps =
	| UserProfileCardForMentionRendererProps
	| UserProfileCardForMentionEditorProps;

/**
 * Profile card for `@user` mentions.
 *
 * Editor mentions are tricky because the underlying mention DOM is owned by ProseMirror
 * (a non-React framework), so a React-trigger-based card can't attach hover / click handlers
 * to it like it would for any normal mention in the renderer. The editor decides when the card
 * should appear (e.g. while a node is selected) and provides the DOM element to anchor to.
 * Meanwhile the renderer (read-only view) renders mentions as plain React elements, where the
 * standard click / hover trigger UX works fine.
 *
 * Rather than make every consumer wire up two different components for these two surfaces,
 * this component dispatches internally based on whether `referenceElement` is provided:
 *
 * - **Renderer (read-only):** omit `referenceElement`. The card opens on click of `children`.
 * - **Editor / nodeview:** pass `referenceElement`. The card stays open while mounted, anchored to
 *   that element. The editor controls open / close by mounting / unmounting this component.
 */
export function UserProfileCardForMention(
	props: UserProfileCardForMentionProps,
): React.JSX.Element {
	return (
		<AtlassianRelayEnvironmentProvider>
			<UserProfileCardForMentionInner {...props} />
		</AtlassianRelayEnvironmentProvider>
	);
}

/** Inner implementation of {@link UserProfileCardForMention}. */
function UserProfileCardForMentionInner({
	userId,
	cloudId,
	orgId,
	viewerId,
	children,
	referenceElement,
	placement = 'bottom-start',
	trigger,
	testId,
	agent,
	isReduced,
	analyticsAttributes,
	prefilledProfileData,
	customerCardDataLoader,
	onOpen,
}: UserProfileCardForMentionProps): React.JSX.Element {
	const environmentProvider = useRelayEnvironmentProvider();

	const onErrorBoundary = useCallback(() => {
		void getPageTime();
	}, []);

	const entryPointErrorFallback = useCallback(
		(_props: { error: Error; onClose: () => void }) => <CardError testId={testId} />,
		[testId],
	);

	const [entryPointReference, setEntryPointReference] = useState<EntryPointReference | null>(null);

	useEffect(() => {
		if (!referenceElement || !userId || !cloudId || !orgId || !viewerId) {
			return;
		}
		const reference = loadEntryPoint(environmentProvider, entryPoint, {
			userId,
			cloudId,
			orgId,
			viewerId,
			isReduced,
		});
		setEntryPointReference(reference);
		return () => {
			reference.dispose();
			setEntryPointReference(null);
		};
	}, [referenceElement, userId, cloudId, orgId, viewerId, isReduced, environmentProvider]);

	// Both render paths require these identifiers to make a valid API call. Bail out to
	// the plain children (mention chip without a card) when any are missing - the editor
	// path also needs this guard before its popper renders.
	if (!userId || !cloudId || !orgId || !viewerId) {
		return <>{children}</>;
	}

	if (!referenceElement) {
		return (
			<UserProfileCardWithTrigger
				userId={userId}
				cloudId={cloudId}
				orgId={orgId}
				viewerId={viewerId}
				placement={placement}
				trigger={trigger}
				testId={testId}
				agent={agent}
				isReduced={isReduced}
				analyticsAttributes={analyticsAttributes}
				prefilledProfileData={prefilledProfileData}
				customerCardDataLoader={customerCardDataLoader}
				onOpen={onOpen}
				shouldRenderToParent={false}
			>
				{children}
			</UserProfileCardWithTrigger>
		);
	}

	return (
		<>
			{children}
			<ErrorBoundary onError={onErrorBoundary} fallback={null}>
				<Portal zIndex={layers.modal()}>
					<ReactPopper referenceElement={referenceElement} placement={placement}>
						{({ ref, style, isReferenceHidden }) => (
							<Box
								ref={ref as React.Ref<HTMLDivElement>}
								// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- popper controls positioning dynamically
								style={style}
								xcss={isReferenceHidden ? mentionPopupStyles.hidden : undefined}
								testId={testId ? `${testId}--mention-popup` : undefined}
							>
								{entryPointReference ? (
									<PopperContent
										entryPointReference={entryPointReference}
										entryPointErrorFallback={entryPointErrorFallback}
										cloudId={cloudId}
										testId={testId}
										agent={agent}
										customerCardDataLoader={customerCardDataLoader}
										isReduced={isReduced}
									/>
								) : (
									<CardLoading isReduced={isReduced} />
								)}
							</Box>
						)}
					</ReactPopper>
				</Portal>
			</ErrorBoundary>
		</>
	);
}
